import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {incomingWaybillGetAll, incomingWaybillExcel} from '../../../redux/slices/product.slice'
import {useMemoizedStaticData} from '../../../utils/performanceOptimizations'

import {pagination} from '../../../helpers'

const selectProductData = (state) => ({
  incomingWaybill: state.product.incomingWaybill,
  loading: state.product.loading,
})

function List() {
  const intl = useIntl()
  const {incomingWaybill, loading} = useSelector(selectProductData)
  const dispatch = useDispatch()
  const pageKey = 'INWAYBILL'

  const [params, setParams] = useState(pagination)

  useEffect(() => {
    if (params) dispatch(incomingWaybillGetAll(params))
  }, [params, dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    dispatch(
      incomingWaybillExcel({
        params,
        fileName: intl.formatMessage({id: 'BC.IN_WAYBILLS'}),
      })
    )
  }, [dispatch, params, intl])

  const columns = useMemo(
    () => [
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'INCOMINGWAYBILL.SEARCH.NAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'name',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'INCOMINGWAYBILL.SEARCH.COMPANYNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'companyName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'INCOMINGWAYBILL.SEARCH.WAYBILLNUMBER'})}
            className='min-w-125px'
          />
        ),
        accessor: 'waybillNumber',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'INCOMINGWAYBILL.SEARCH.ISSUEDATE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'issueDate',
        Cell: ({...props}) => <CustomCell item={props} date='short' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
            className='min-w-125px'
          />
        ),
        accessor: 'lastModifiedBy',
        Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
      },
    ],
    [intl, handleSorting]
  )

  const actionButtons = useMemoizedStaticData(
    () => [
      {
        title: 'BUTTON.EXCEL',
        onClick: handleExcelExport,
        icon: '/images/icons/duotune/arrows/arr044.svg',
        variant: 'light-primary',
      },
    ],
    [handleExcelExport]
  )

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.IN_WAYBILLS'})}</PageTitle>
      <KTCard>
        <ListHeader search={handleSearch} actionButtons={actionButtons} pages={pageKey} />
        <KTCardBody>
          {!incomingWaybill && <ListLoading />}
          {incomingWaybill && (
            <Table
              items={incomingWaybill}
              cols={columns}
              handlePagination={handlePagination}
              paddless
              pages={pageKey}
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
